import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { Fetch } from '../utils/Utils.js';
import { formatDate } from '../../utils/dateFormat.js';
import '../charts/reproduction/NivoChartCard.css';
import '../charts/herd/CategoricalDonut.css';
import './FinanceCharts.css';
import ProjectBalancesChart from './projects/ProjectBalancesChart.js';

// Gain/loss and positive/negative net are STATE, not identity — status
// palette (palette.md), the same good/critical steps already used for
// reproduction outcomes in ReproductionRecentResults.js.
const GOOD = '#0ca30c';
const CRITICAL = '#d03b3b';

// Finance category is identity — fixed categorical hue order, same slots
// (and same fold-to-"Other" past 8) as every other categorical donut in the
// app (HerdByBreed.js, HerdByCauseOfDeath.js).
const CATEGORY_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const MAX_SLICES = 8;

const MONTHS = 12;

const getLast12Months = () => {
  const months = [];
  const now = new Date();
  for (let i = MONTHS - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = formatDate(d, { month: 'short', year: '2-digit' });
    months.push({ key, label });
  }
  return months;
};

const getCurrentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Matches the currency formatting already used everywhere else in Finances
// (ListFinances.js: `${symbol}${amount.toFixed(2)}`, no thousands separator).
const fmt = (symbol, n) => `${symbol}${Number(n || 0).toFixed(2)}`;
const fmtSigned = (symbol, n) => `${n >= 0 ? '+' : '-'}${symbol}${Math.abs(Number(n || 0)).toFixed(2)}`;

// Hero center label for the category donuts — a smaller inline size than
// CategoricalDonut.css's .hb-hero-value (32px), since a currency string runs
// much longer than HerdByBreed's plain integer count and would overflow the
// donut hole at the shared size.
const FinanceCenterLabel = (amountLabel, subLabel) => ({ centerX, centerY }) => (
  <g>
    <text
      x={centerX} y={centerY - 6} textAnchor="middle" dominantBaseline="middle"
      style={{ fontSize: 18, fontWeight: 700, fill: '#1a1714', fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}
    >
      {amountLabel}
    </text>
    <text x={centerX} y={centerY + 14} textAnchor="middle" dominantBaseline="middle" className="hb-hero-label">
      {subLabel}
    </text>
  </g>
);

// One category donut (gains OR losses for the current month) — reuses the
// exact hb-body/hb-donut-wrap/hb-legend structure from HerdByBreed.js so it
// looks identical to every other categorical donut in the app, just with a
// currency hero instead of a count.
const CategoryDonut = ({ slices, total, heroLabel, symbol }) => {
  if (total === 0) return <p className="nivo-card-empty">—</p>;

  const pieData = slices.map(s => ({ id: s.cat, label: s.cat, value: s.amount, color: s.color }));

  return (
    <div className="hb-body">
      <div className="hb-donut-wrap">
        <ResponsivePie
          data={pieData}
          margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
          innerRadius={0.62}
          padAngle={1.6}
          cornerRadius={4}
          activeOuterRadiusOffset={6}
          colors={{ datum: 'data.color' }}
          borderWidth={0}
          enableArcLinkLabels={false}
          arcLabelsSkipAngle={26}
          arcLabelsTextColor="#1a1714"
          arcLabel={d => `${Math.round((d.value / total) * 100)}%`}
          motionConfig="gentle"
          layers={['arcs', 'arcLabels', FinanceCenterLabel(fmt(symbol, total), heroLabel)]}
          tooltip={({ datum }) => (
            <div className="nivo-tooltip">
              <span className="nivo-tooltip-swatch" style={{ background: datum.data.color }} />
              <strong>{datum.label}</strong>: {fmt(symbol, datum.value)} ({Math.round((datum.value / total) * 100)}%)
            </div>
          )}
        />
      </div>

      <ul className="hb-legend">
        {slices.map(s => (
          <li key={s.cat} className="hb-legend-row" style={{ '--row-accent': s.color }}>
            <span className="hb-legend-dot" style={{ background: s.color }} />
            <span className="hb-legend-text">
              <span className="hb-legend-headline">
                <strong className="hb-legend-percent">{s.percent}%</strong> {s.cat}
              </span>
              <span className="hb-legend-count">{fmt(symbol, s.amount)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const FinanceCharts = ({ currencySymbol = 'R$' }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);

  useEffect(() => {
    Fetch('api/data/finance.transactions', 'GET', null,
      (d) => setData(d || []),
      () => {}
    );
  }, []);

  // --- Chart 1: Gains & Losses by month ---
  const months = getLast12Months();
  const monthlyGains = {};
  const monthlyLosses = {};
  months.forEach(m => { monthlyGains[m.key] = 0; monthlyLosses[m.key] = 0; });

  data.forEach(row => {
    const ym = row.date?.substring(0, 7);
    if (!ym || !monthlyGains.hasOwnProperty(ym)) return;
    if (row.type === 'gain') monthlyGains[ym] += Number(row.amount || 0);
    else monthlyLosses[ym] += Number(row.amount || 0);
  });

  // label is pre-formatted onto each bar's own data (not left to axisBottom's
  // format callback) — avoids the "raw YYYY-MM slips through" bug class
  // found in TemperatureChart.js/ReproductionSuccessByMonthAndGroup.js.
  const barData = months.map(m => ({
    label: m.label,
    gains: Math.round(monthlyGains[m.key] * 100) / 100,
    losses: Math.round(monthlyLosses[m.key] * 100) / 100,
  }));
  const hasMonthlyData = barData.some(d => d.gains > 0 || d.losses > 0);

  const netProfit12mo = months.reduce((sum, m) => sum + monthlyGains[m.key] - monthlyLosses[m.key], 0);
  const profitSummary = hasMonthlyData
    ? t('chart_summary_finance_profit', { amount: fmtSigned(currencySymbol, netProfit12mo) })
    : '';

  // --- Chart 2: This month by category ---
  const currentYM = getCurrentYearMonth();
  const thisMonth = data.filter(row => row.date?.substring(0, 7) === currentYM);

  const buildCategorySlices = (rows) => {
    const map = {};
    rows.forEach(row => {
      const cat = row.category ? t(row.category, { defaultValue: row.category }) : t('other');
      map[cat] = (map[cat] || 0) + Number(row.amount || 0);
    });
    const ranked = Object.entries(map).sort((a, b) => b[1] - a[1]);
    const top = ranked.slice(0, MAX_SLICES);
    const rest = ranked.slice(MAX_SLICES);
    if (rest.length > 0) {
      const otherTotal = rest.reduce((sum, [, amount]) => sum + amount, 0);
      top.push([t('other_categories'), otherTotal]);
    }
    const total = top.reduce((sum, [, amount]) => sum + amount, 0);
    return top.map(([cat, amount], i) => ({
      cat, amount,
      percent: total > 0 ? Math.round((amount / total) * 100) : 0,
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
  };

  const gainSlices = buildCategorySlices(thisMonth.filter(r => r.type === 'gain'));
  const lossSlices = buildCategorySlices(thisMonth.filter(r => r.type !== 'gain'));
  const gainTotal = gainSlices.reduce((s, x) => s + x.amount, 0);
  const lossTotal = lossSlices.reduce((s, x) => s + x.amount, 0);
  const hasMonthData = gainSlices.length > 0 || lossSlices.length > 0;
  const monthSummary = hasMonthData
    ? t('chart_summary_finance_month', { gains: fmt(currencySymbol, gainTotal), losses: fmt(currencySymbol, lossTotal) })
    : '';

  // --- Chart 3: All-time net by institution ---
  const institutionNetMap = {};
  data.forEach(row => {
    const inst = row.institution ? t(row.institution, { defaultValue: row.institution }) : t('institution_other');
    const amount = Number(row.amount || 0);
    institutionNetMap[inst] = (institutionNetMap[inst] || 0) + (row.type === 'gain' ? amount : -amount);
  });
  const instData = Object.entries(institutionNetMap)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([institution, net]) => ({ institution, net: Math.round(net * 100) / 100 }));
  const instHeight = Math.max(120, instData.length * 48);
  // Nivo's 'auto' valueScale min/max resolves to 0 for this data shape
  // instead of the true negative extent (confirmed by inspecting rendered
  // bar geometry — the zero baseline pinned to the left plot edge instead of
  // the real min) — pin the domain explicitly from the data instead of
  // trusting auto-detection, same reasoning as ProtocolSuccessRate.js's
  // fixed min:0/max:100.
  const instValues = instData.map(d => d.net).concat(0);
  const instMin = Math.min(...instValues);
  const instMax = Math.max(...instValues);

  const topInst = instData.length > 0
    ? instData.reduce((max, d) => (d.net > max.net ? d : max), instData[0])
    : null;
  const instSummary = topInst
    ? t('chart_summary_finance_institution', { institution: topInst.institution, amount: fmtSigned(currencySymbol, topInst.net) })
    : '';

  return (
    <div className="finance-charts">
      <div className="Chart NivoCard">
        <h3>{t('profit_by_month')}</h3>
        {profitSummary && <p className="nivo-card-subtitle">{profitSummary}</p>}

        {hasMonthlyData ? (
          <>
            <ul className="nivo-legend-row">
              <li><span className="nivo-legend-dot" style={{ background: GOOD }} />{t('gains')}</li>
              <li><span className="nivo-legend-dot" style={{ background: CRITICAL }} />{t('losses')}</li>
            </ul>
            <div style={{ height: 300 }}>
              <ResponsiveBar
                data={barData}
                keys={['gains', 'losses']}
                indexBy="label"
                groupMode="grouped"
                margin={{ top: 12, right: 16, bottom: 42, left: 60 }}
                padding={0.3}
                innerPadding={2}
                borderRadius={3}
                colors={({ id }) => (id === 'gains' ? GOOD : CRITICAL)}
                axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: -30 }}
                axisLeft={{ tickSize: 0, tickPadding: 8, format: (v) => `${currencySymbol}${v}` }}
                enableGridY={true}
                enableLabel={false}
                motionConfig="gentle"
                theme={{
                  axis: { ticks: { text: { fill: '#898781', fontSize: 11.5 } } },
                  grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
                }}
                tooltip={({ id, value, indexValue }) => (
                  <div className="nivo-tooltip">
                    <span className="nivo-tooltip-swatch" style={{ background: id === 'gains' ? GOOD : CRITICAL }} />
                    <strong>{id === 'gains' ? t('gains') : t('losses')}</strong> ({indexValue}): <span className="nivo-tooltip-value">{fmt(currencySymbol, value)}</span>
                  </div>
                )}
              />
            </div>
          </>
        ) : (
          <p className="nivo-card-empty">{t('chart_summary_no_finance_data')}</p>
        )}
      </div>

      <div className="Chart NivoCard">
        <h3>{t('this_month_by_category')}</h3>
        {monthSummary && <p className="nivo-card-subtitle">{monthSummary}</p>}

        {hasMonthData ? (
          <div className="finance-pie-row">
            <div className="finance-pie-half">
              <div className="finance-pie-subtitle">{t('gains')}</div>
              <CategoryDonut slices={gainSlices} total={gainTotal} heroLabel={t('gains')} symbol={currencySymbol} />
            </div>
            <div className="finance-pie-half">
              <div className="finance-pie-subtitle">{t('losses')}</div>
              <CategoryDonut slices={lossSlices} total={lossTotal} heroLabel={t('losses')} symbol={currencySymbol} />
            </div>
          </div>
        ) : (
          <p className="nivo-card-empty">{t('chart_summary_no_finance_data')}</p>
        )}
      </div>

      <div className="Chart NivoCard">
        <h3>{t('net_by_institution')}</h3>
        {instSummary && <p className="nivo-card-subtitle">{instSummary}</p>}

        {instData.length > 0 ? (
          <div style={{ height: instHeight }}>
            <ResponsiveBar
              data={instData}
              keys={['net']}
              indexBy="institution"
              layout="horizontal"
              groupMode="grouped"
              valueScale={{ type: 'linear', min: instMin, max: instMax }}
              margin={{ top: 8, right: 36, bottom: 36, left: 110 }}
              padding={0.35}
              borderRadius={4}
              colors={(bar) => (bar.data.net >= 0 ? GOOD : CRITICAL)}
              enableGridY={false}
              axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: 5, format: (v) => `${currencySymbol}${Math.round(v)}` }}
              axisLeft={{ tickSize: 0, tickPadding: 8 }}
              label={(d) => fmtSigned(currencySymbol, d.value)}
              labelSkipWidth={54}
              labelTextColor="#ffffff"
              motionConfig="gentle"
              theme={{
                axis: { ticks: { text: { fill: '#898781', fontSize: 12 } } },
                grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
                labels: { text: { fontWeight: 700, fontSize: 11.5 } },
              }}
              tooltip={({ indexValue, value }) => (
                <div className="nivo-tooltip">
                  <span className="nivo-tooltip-swatch" style={{ background: value >= 0 ? GOOD : CRITICAL }} />
                  <strong>{indexValue}</strong>: <span className="nivo-tooltip-value">{fmtSigned(currencySymbol, value)}</span>
                </div>
              )}
            />
          </div>
        ) : (
          <p className="nivo-card-empty">{t('chart_summary_no_finance_data')}</p>
        )}
      </div>

      <ProjectBalancesChart currencySymbol={currencySymbol} />
    </div>
  );
};

export default FinanceCharts;
